import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, ChangeDetectionStrategy } from "@angular/core";

import { MentionCandidate } from "@mypxplat/xplat/core";

/**
 * The @mention dropdown, for every surface.
 *
 * Purely presentational: it renders candidates and reports clicks. Keyboard
 * navigation stays with the input that owns the caret, because the editor never
 * loses focus while the picker is open -- the picker is not focusable at all.
 *
 * It is rendered inside a CDK overlay attached to the body (see
 * `MentionOverlayController`), which is what stops it being clipped by the
 * `overflow: hidden` container around the docked workspace and file chat inputs.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-mention-picker",
  templateUrl: "mention-picker.component.html",
  standalone: false,
})
export class MentionPickerComponent {
  @Input() candidates: MentionCandidate[] = [];
  /** Echoed into each row's DOM id so the editor can point aria-activedescendant at it. */
  @Input() idPrefix: string = "mention";

  @Output() picked: EventEmitter<MentionCandidate> = new EventEmitter();

  @ViewChild("panel", { static: true }) panel: ElementRef;

  /**
   * Keeps the keyboard-highlighted row inside the scroll window.
   *
   * The panel scrolls once there are more candidates than fit, and arrowing past
   * the last visible row moved the highlight somewhere the user couldn't see --
   * so Enter selected someone off-screen.
   *
   * Scrolls the panel directly rather than using `scrollIntoView`, which will
   * happily scroll ancestors (and the page behind the overlay) too. Measured
   * from bounding rects so it doesn't depend on which ancestor is the
   * `offsetParent` inside the CDK overlay.
   */
  scrollSelectedIntoView() {
    const panel: HTMLElement = this.panel && this.panel.nativeElement;
    if (!panel) return;

    const row: HTMLElement = panel.querySelector(".mention-option.selected");
    if (!row) return;

    const panelBox = panel.getBoundingClientRect();
    const rowBox = row.getBoundingClientRect();

    if (rowBox.top < panelBox.top) {
      panel.scrollTop -= panelBox.top - rowBox.top;
    } else if (rowBox.bottom > panelBox.bottom) {
      panel.scrollTop += rowBox.bottom - panelBox.bottom;
    }
  }

  optionId(index: number) {
    return this.idPrefix + "-option-" + index;
  }

  /**
   * Mousedown would blur the editor before the click lands, which tears down the
   * overlay and loses the caret. Suppress it and act on the click instead.
   */
  preventBlur(event: MouseEvent) {
    event.preventDefault();
  }

  select(candidate: MentionCandidate) {
    this.picked.next(candidate);
  }
}
